import { FileValidation, ExportFormat, Vehicle, ProcessingStats } from '../types';

// File validation constants
const MAX_FILE_SIZE = 500 * 1024 * 1024; // 500MB
const ALLOWED_TYPES = ['video/mp4', 'video/avi', 'video/quicktime', 'video/x-msvideo', 'video/webm', 'video/x-matroska'];
const ALLOWED_EXTENSIONS = ['.mp4', '.avi', '.mov', '.webm', '.mkv'];

/**
 * Validate video file
 */
export function validateVideoFile(file: File): FileValidation {
  // Check file type
  const extension = '.' + file.name.split('.').pop()?.toLowerCase();
  const isValidType = ALLOWED_TYPES.includes(file.type) || ALLOWED_EXTENSIONS.includes(extension);
  
  if (!isValidType) {
    return {
      valid: false,
      error: `Invalid file type. Allowed: ${ALLOWED_EXTENSIONS.join(', ')}`
    };
  }

  // Check file size
  if (file.size > MAX_FILE_SIZE) {
    return {
      valid: false,
      error: `File too large. Maximum size: ${formatFileSize(MAX_FILE_SIZE)}`
    };
  }

  return { valid: true };
}

/**
 * Format file size to human-readable string
 */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B';
  
  const units = ['B', 'KB', 'MB', 'GB'];
  const k = 1024;
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${units[i]}`;
}

/**
 * Format duration to MM:SS or HH:MM:SS
 */
export function formatDuration(seconds: number): string {
  const hrs = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);

  if (hrs > 0) {
    return `${hrs.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }
  
  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
}

/**
 * Format speed with unit
 */
export function formatSpeed(speed: number, unit: 'kmh' | 'mph' = 'kmh'): string {
  const converted = unit === 'mph' ? speed * 0.621371 : speed;
  const unitLabel = unit === 'mph' ? 'mph' : 'km/h';
  return `${converted.toFixed(1)} ${unitLabel}`;
}

/**
 * Generate unique ID
 */
export function generateId(): string {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * Debounce function
 */
export function debounce<T extends (...args: unknown[]) => unknown>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: ReturnType<typeof setTimeout> | null = null;

  return (...args: Parameters<T>) => {
    if (timeout) clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
}

/**
 * Throttle function
 */
export function throttle<T extends (...args: unknown[]) => unknown>(
  func: T,
  limit: number
): (...args: Parameters<T>) => void {
  let inThrottle = false;

  return (...args: Parameters<T>) => {
    if (!inThrottle) {
      func(...args);
      inThrottle = true;
      setTimeout(() => (inThrottle = false), limit);
    }
  };
}

/**
 * Calculate color based on speed (green -> yellow -> red)
 */
export function getSpeedColor(speed: number, maxSpeed: number = 120): string {
  const ratio = Math.min(speed / maxSpeed, 1);
  
  if (ratio < 0.5) {
    // Green to Yellow
    const r = Math.floor(255 * (ratio * 2));
    const g = 255;
    return `rgb(${r}, ${g}, 100)`;
  } else {
    // Yellow to Red
    const r = 255;
    const g = Math.floor(255 * (1 - (ratio - 0.5) * 2));
    return `rgb(${r}, ${g}, 100)`;
  }
}

/**
 * Export data to different formats
 */
export async function exportData(
  format: ExportFormat,
  vehicles: Vehicle[],
  stats: ProcessingStats
): Promise<void> {
  switch (format) {
    case 'json':
      downloadJSON({ vehicles, stats });
      break;
    case 'csv':
      downloadCSV(vehicles);
      break;
    case 'pdf':
      await generatePDF(vehicles, stats);
      break;
  }
}

function downloadJSON(data: object): void {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
  downloadBlob(blob, 'vehicle-detection-report.json');
}

function downloadCSV(vehicles: Vehicle[]): void {
  const headers = ['ID', 'Type', 'Detections', 'Avg Speed (km/h)', 'Max Speed (km/h)', 'Min Speed (km/h)', 'Confidence'];
  const rows = vehicles.map(v => [
    v.id,
    v.vehicle_type || 'Unknown',
    v.detections_count,
    v.avg_speed.toFixed(2),
    v.max_speed.toFixed(2),
    v.min_speed.toFixed(2),
    (v.avg_confidence * 100).toFixed(1) + '%'
  ]);

  const csv = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  downloadBlob(blob, 'vehicle-detection-report.csv');
}

async function generatePDF(vehicles: Vehicle[], stats: ProcessingStats): Promise<void> {
  // Simple HTML to PDF using print
  const content = `
    <!DOCTYPE html>
    <html>
    <head>
      <title>Vehicle Detection Report</title>
      <style>
        body { font-family: Arial, sans-serif; padding: 20px; }
        h1 { color: #6366f1; }
        table { width: 100%; border-collapse: collapse; margin-top: 20px; }
        th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
        th { background-color: #6366f1; color: white; }
        .stats { display: grid; grid-template-columns: repeat(3, 1fr); gap: 15px; margin: 20px 0; }
        .stat { background: #f3f4f6; padding: 15px; border-radius: 8px; }
        .stat-value { font-size: 24px; font-weight: bold; color: #6366f1; }
      </style>
    </head>
    <body>
      <h1>🚗 Vehicle Speed Detection Report</h1>
      <p>Generated: ${new Date().toLocaleString()}</p>
      
      <div class="stats">
        <div class="stat">
          <div class="stat-value">${stats.total_vehicles}</div>
          <div>Total Vehicles</div>
        </div>
        <div class="stat">
          <div class="stat-value">${stats.avg_speed.toFixed(1)} km/h</div>
          <div>Average Speed</div>
        </div>
        <div class="stat">
          <div class="stat-value">${stats.max_speed.toFixed(1)} km/h</div>
          <div>Maximum Speed</div>
        </div>
      </div>

      <h2>Vehicle Details</h2>
      <table>
        <tr>
          <th>ID</th>
          <th>Type</th>
          <th>Detections</th>
          <th>Avg Speed</th>
          <th>Max Speed</th>
          <th>Confidence</th>
        </tr>
        ${vehicles.map(v => `
          <tr>
            <td>${v.id}</td>
            <td>${v.vehicle_type || 'Unknown'}</td>
            <td>${v.detections_count}</td>
            <td>${v.avg_speed.toFixed(1)} km/h</td>
            <td>${v.max_speed.toFixed(1)} km/h</td>
            <td>${(v.avg_confidence * 100).toFixed(1)}%</td>
          </tr>
        `).join('')}
      </table>
    </body>
    </html>
  `;

  const printWindow = window.open('', '_blank');
  if (printWindow) {
    printWindow.document.write(content);
    printWindow.document.close();
    printWindow.print();
  }
}

function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/**
 * Get video thumbnail
 */
export function getVideoThumbnail(file: File): Promise<string> {
  return new Promise((resolve) => {
    const video = document.createElement('video');
    video.src = URL.createObjectURL(file);
    video.muted = true;
    video.currentTime = 1;

    video.onloadeddata = () => {
      const canvas = document.createElement('canvas');
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      
      const ctx = canvas.getContext('2d');
      ctx?.drawImage(video, 0, 0);
      
      resolve(canvas.toDataURL('image/jpeg'));
      URL.revokeObjectURL(video.src);
    };

    video.onerror = () => {
      resolve('');
    };
  });
}

/**
 * Get video metadata
 */
export function getVideoMetadata(file: File): Promise<{
  duration: number;
  width: number;
  height: number;
}> {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video');
    video.src = URL.createObjectURL(file);
    video.muted = true;

    video.onloadedmetadata = () => {
      resolve({
        duration: video.duration,
        width: video.videoWidth,
        height: video.videoHeight
      });
      URL.revokeObjectURL(video.src);
    };

    video.onerror = () => {
      reject(new Error('Failed to load video metadata'));
      URL.revokeObjectURL(video.src);
    };
  });
}

/**
 * Clamp number between min and max
 */
export function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

/**
 * Linear interpolation
 */
export function lerp(start: number, end: number, t: number): number {
  return start + (end - start) * clamp(t, 0, 1);
}
